import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import posthog from '../lib/posthog';
import { IS_FLAVORED } from '../lib/flavor';
import { AFFILIATE_TOKEN_STORAGE_KEY, readStoredAffiliateToken } from '../lib/affiliateToken';
import { clearPendingTryChat } from '../lib/tryChatHandoff';
import { releasePushToken } from '../lib/device/push';
import type { Profile, UserRole } from '../types';

export function availableRoles(role: UserRole, roles?: UserRole[]): UserRole[] {
  if (roles?.length) return roles;
  if (role === 'admin') return ['admin', 'host', 'traveler'];
  if (role === 'host') return ['host', 'traveler'];
  return ['traveler'];
}

/**
 * The roles this build offers. Web: everything the account has. Native app:
 * traveler and host only (never admin), always at least traveler.
 */
export function offeredRoles(role: UserRole, roles: UserRole[] | undefined, native: boolean): UserRole[] {
  const all = availableRoles(role, roles);
  if (!native) return all;
  const allowed = all.filter((r) => r !== 'admin');
  return allowed.length ? allowed : ['traveler'];
}

/**
 * The role to open in: the saved choice if this build offers it. A saved role
 * this build doesn't offer (admin, in the native app) opens as traveler: the app
 * never puts someone in host mode they didn't choose. With no saved choice,
 * the highest role this build offers.
 */
export function resolveActiveRole(
  offered: UserRole[],
  active: UserRole | null | undefined,
  highest: UserRole,
): UserRole {
  if (active && offered.includes(active)) return active;
  if (active && offered.includes('traveler')) return 'traveler';
  if (offered.includes(highest)) return highest;
  return offered.includes('host') ? 'host' : offered[0];
}

interface AuthState {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  activeRole: UserRole;
  roles: UserRole[];
  switchRole: (role: UserRole) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function useAuthProvider(): AuthState {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const identifiedUserId = useRef<string | null>(null);

  function identifyUser(authUser: User) {
    if (identifiedUserId.current === authUser.id) return;
    if (identifiedUserId.current) posthog.reset();

    posthog.identify(authUser.id, authUser.email ? { email: authUser.email } : undefined);
    identifiedUserId.current = authUser.id;
  }

  async function fetchProfile(userId: string) {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    setProfile(data ?? null);
    void claimAffiliateReferral();
  }

  // Best-effort, fire-and-forget: attributes this session to whichever
  // affiliate QR code was scanned before signup, if any and not yet expired.
  // claim_affiliate_referral is idempotent (only ever sets an unset
  // referred_by_affiliate_id), so calling this on every login is harmless —
  // the stored token is cleared either way once we've tried, so it only
  // ever fires once per QR-code visit regardless of how many times the
  // person logs in afterward.
  async function claimAffiliateReferral() {
    const token = readStoredAffiliateToken();
    if (!token) return;
    try {
      await supabase.rpc('claim_affiliate_referral', { p_token: token });
    } catch {
      /* best effort */
    } finally {
      try { localStorage.removeItem(AFFILIATE_TOKEN_STORAGE_KEY); } catch { /* ignore */ }
    }
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        identifyUser(session.user);
        fetchProfile(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        identifyUser(session.user);
        fetchProfile(session.user.id);
      } else {
        if (event === 'SIGNED_OUT') {
          posthog.reset();
          identifiedUserId.current = null;
        }
        setProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchRole = useCallback(async (role: UserRole) => {
    if (!user) return;
    const previous = profile;
    setProfile((prev) => prev ? { ...prev, active_role: role } : prev);
    const { error } = await supabase
      .from('profiles')
      .update({ active_role: role, updated_at: new Date().toISOString() })
      .eq('id', user.id);
    if (error) {
      console.error('switchRole failed:', error);
      setProfile(previous);
    }
  }, [user, profile]);

  async function signOut() {
    // /try's pending draft (sekretly_try_chat) is keyed globally, not per
    // account — left in place across sign-out, it gets wrongly picked up and
    // "resumed" by whichever account next visits /try in this browser,
    // instead of that account getting its own fresh greeting.
    clearPendingTryChat();
    // Pushes follow whoever is signed in: let go of this phone while the session still exists.
    await releasePushToken();
    await supabase.auth.signOut();
  }

  const highestRole: UserRole = profile?.role ?? 'traveler';
  const roles = offeredRoles(highestRole, profile?.roles, IS_FLAVORED);
  const activeRole = resolveActiveRole(roles, profile?.active_role as UserRole | null, highestRole);

  return { user, profile, loading, activeRole, roles, switchRole, signOut };
}

export { AuthContext };
